// IR (see mps803.js) -> 1-bit page bitmaps, 1 pixel = 1 printer dot.
//
// Geometry (as VICE's MPS-803 driver): 480 dots across, 7 pin rows per text
// line plus 3 rows of paper feed (6 lpi) or none in bit image mode (9 lpi).
// Paper is one continuous strip cut every `pageRows` dot rows, like fanfold
// paper at the perforation; a page-break marker feeds to the next cut.

import { DOTS_PER_LINE, CHAR_WIDTH } from '../core/mps803.js';

export const PIN_ROWS = 7;
export const FONT_GLYPHS = 512;
export const FONT_BYTES = FONT_GLYPHS * CHAR_WIDTH;
export const DEFAULT_PAGE_ROWS = 792; // 11 inch at 72 dpi vertical

export class FontMissingError extends Error {}

// Fetches web/mps803_font.bin. Throws FontMissingError with instructions
// instead of silently rendering nothing.
export async function loadFont(url = 'mps803_font.bin') {
  let res;
  try {
    res = await fetch(url);
  } catch (e) {
    throw new FontMissingError(`MPS-803 font could not be loaded (${e.message}). Run tools/fetch_font.sh.`);
  }
  if (!res.ok) {
    throw new FontMissingError(`MPS-803 font not found (${url}: HTTP ${res.status}). Run tools/fetch_font.sh to create it.`);
  }
  const font = new Uint8Array(await res.arrayBuffer());
  if (font.length !== FONT_BYTES) {
    throw new FontMissingError(`MPS-803 font has the wrong size (${font.length}, expected ${FONT_BYTES}). Re-run tools/fetch_font.sh.`);
  }
  return font;
}

// A page-break marker for the `lines` array.
export const PAGE_BREAK = Object.freeze({ pageBreak: true });

// Places lines on the strip. Returns { placed: [{line, y}], totalRows }.
export function layout(lines, pageRows = DEFAULT_PAGE_ROWS) {
  const placed = [];
  let y = 0;
  for (const line of lines) {
    if (line.pageBreak) {
      if (y % pageRows !== 0) y = Math.ceil(y / pageRows) * pageRows;
      continue;
    }
    placed.push({ line, y });
    y += PIN_ROWS + line.gapRows;
  }
  return { placed, totalRows: y };
}

// Rows all lines need on one continuous sheet, page breaks ignored: the page length
// at which everything printed so far fits on a single page (banner prints).
export function contentRows(lines) {
  let y = 0;
  for (const line of lines) if (!line.pageBreak) y += PIN_ROWS + line.gapRows;
  return y;
}

function drawColumn(bm, x, y, bits, invert) {
  if (x < 0 || x >= bm.width) return;
  for (let r = 0; r < PIN_ROWS; r++) {
    let on = (bits >> r) & 1;
    if (invert) on ^= 1;
    if (y + r < bm.height) bm.data[(y + r) * bm.width + x] = on;
  }
}

function drawItem(bm, font, y, it) {
  if (it.kind === 'bits') {
    drawColumn(bm, it.x, y, it.value, false);
    return;
  }
  const glyph = (it.set === 'business' ? 256 : 0) + it.code;
  const rep = it.wide ? 2 : 1;
  for (let c = 0; c < CHAR_WIDTH; c++) {
    const bits = font[glyph * CHAR_WIDTH + c] ?? 0;
    for (let k = 0; k < rep; k++) drawColumn(bm, it.x + c * rep + k, y, bits, it.reverse);
  }
}

// Renders everything and cuts it into pages. Returns an array of
// { width, height, data } where data has one byte per pixel, 1 = black.
// The last page ends after the paper feed of the last line.
export function renderPages(lines, font, pageRows = DEFAULT_PAGE_ROWS) {
  const { placed, totalRows } = layout(lines, pageRows);
  if (totalRows === 0) return [];
  const pageCount = Math.ceil(totalRows / pageRows);
  const pages = [];
  for (let p = 0; p < pageCount; p++) {
    const height = p === pageCount - 1 ? totalRows - p * pageRows : pageRows;
    pages.push({ width: DOTS_PER_LINE, height, data: new Uint8Array(DOTS_PER_LINE * height) });
  }
  for (const { line, y } of placed) {
    // A line may straddle the cut between two pages: draw it into both.
    const first = Math.floor(y / pageRows);
    const last = Math.floor((y + PIN_ROWS - 1) / pageRows);
    for (let p = first; p <= last && p < pageCount; p++) {
      for (const it of line.items) drawItem(pages[p], font, y - p * pageRows, it);
    }
  }
  return pages;
}

// 1-bit bitmap -> ImageData-compatible RGBA (black on white).
export function toRGBA(bm) {
  const out = new Uint8ClampedArray(bm.width * bm.height * 4);
  for (let i = 0; i < bm.data.length; i++) {
    const v = bm.data[i] ? 0 : 255;
    out[i * 4] = out[i * 4 + 1] = out[i * 4 + 2] = v;
    out[i * 4 + 3] = 255;
  }
  return out;
}
